import React, { useMemo } from 'react';

import usePage from '@dashboard/hooks/usePage';
import useUrlState from '@dashboard/hooks/useUrlState';
import useTableSort from '@dashboard/hooks/useTableSort';
import { getStatusFromQuery } from '@dashboard/lib/tableHelpers';

import { PaymentsChargesList } from '@dashboard/scenes/PaymentsCharges';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import ExportView from '@dashboard/components/ExportView';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import SearchInput from '@dashboard/components/Form/components/SearchInput';

const FILTER_OPTIONS = {
  ALL: { value: 'all', label: 'All' },
  PAID: { value: 'paid', label: 'Paid' },
  PARTIALLY_PAID: { value: 'partially_paid', label: 'Partially paid' },
  RECOVERED: { value: 'recovered', label: 'Recovered' },
  UNPAID: { value: 'unpaid', label: 'Unpaid' },
  INCOMPLETE: { value: 'incomplete', label: 'Incomplete' },
  FAILED: { value: 'failed', label: 'Failed' },
  REFUNDED: { value: 'refunded', label: 'Refunded' },
  PARTIAL_REFUND: { value: 'partial_refund', label: 'Partial refund' },
  CANCELED: { value: 'canceled', label: 'Canceled' },
  ABANDONED: { value: 'abandoned', label: 'Abandoned' },
};

const PagePayments = () => {
  const { page } = usePage();
  const [query, setParams] = useUrlState();
  const { sorting, onSortingChange, sort } = useTableSort();

  const search = query.search || '';
  const status = getStatusFromQuery(query.status, FILTER_OPTIONS);

  const filter = useMemo(
    () => ({
      ...(FILTER_OPTIONS[status].value !== 'all' && {
        status: FILTER_OPTIONS[status].value,
      }),
      page: page._id,
      livemode: true,
      search,
    }),
    [status, page._id, search],
  );

  const handleSearchChange = value => setParams({ search: value || undefined });

  const handleStatusChange = key =>
    setParams({
      status: FILTER_OPTIONS[key].value === 'all' ? undefined : FILTER_OPTIONS[key].value,
    });

  return (
    <div className="mb-24">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pb-4">
        <CardHeaderTitle variant="page">
          {page.type === 'checkout' ? 'Payments' : 'Bookings'}
        </CardHeaderTitle>

        <div className="flex flex-wrap items-center gap-2 max-lg:w-full [&>*:first-child]:min-w-0 max-lg:[&>*:first-child]:flex-1 max-lg:[&>*:first-child]:basis-56 max-sm:[&>*:first-child]:basis-full">
          <SearchInput
            onChange={handleSearchChange}
            name="search_customers"
            placeholder="Search by email"
            initialValue={search}
          />
          <FilterInput options={FILTER_OPTIONS} onChange={handleStatusChange} value={status} />
          <ExportView
            collection="payments"
            label="payments"
            filters={{
              ...filter,
              page: page._id,
              sort: JSON.stringify(sort),
            }}
          />
        </div>
      </div>

      <PaymentsChargesList
        pageType={page.type}
        sort={sort}
        search={search}
        filter={filter}
        options={{ hidePaymentLink: true }}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
};

export default PagePayments;
